import {
    FC,
    ReactElement,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { Modal } from 'reactstrap'

import { ModalContent, PacoModal } from '../../../../@paco/compositions'
import AddCommentForm from '../../../../@paco/containers/@forms/AddCommentForm/AddCommentForm'
import { BaseScheduleDetail } from '../../../../@paco/entities/BaseSchedule/BaseSchedule'
import { AddCommentFormData } from '../../../../@paco/entities/Comment/Comment'
import { addYears, isSameDay, startOfYear } from '../../../../@paco/helpers/date'
import checkPermission from '../../../../@paco/helpers/permissions/checkPermission'
import trans from '../../../../@paco/helpers/trans'
import {
    addBaseScheduleComment,
    deleteBaseScheduleComment,
    editBaseSchedule,
    getBaseSchedule,
} from '../../../../@paco/redux/baseSchedule/baseScheduleActions'
import {
    setBaseSchedule,
    setError,
    setIsSuccessful,
    setLastModifiedBaseScheduleShiftId,
    setSelectedWeekIndex,
} from '../../../../@paco/redux/baseSchedule/baseScheduleReducer'
import {
    copyBaseScheduleShift,
    createBaseScheduleShift,
} from '../../../../@paco/redux/baseScheduleShift/baseScheduleShiftActions'
import {
    setDeletedBaseScheduleShiftId,
    setIsDeleteBaseScheduleShiftSuccessful,
    setIsSuccessful as setBaseScheduleShiftIsSuccessful,
} from '../../../../@paco/redux/baseScheduleShift/baseScheduleShiftReducer'
import { useTypedDispatch, useTypedSelector } from '../../../../@paco/redux/store'
import { setToast } from '../../../../@paco/redux/toasts/toastsReducer'
import { ToastType } from '../../../../@paco/types'

import SidebarPage from '../../../../components/SidebarPage/SidebarPage'
import { transformBaseScheduleDetailToViewModel } from '../../../../entities/BaseSchedule/BaseScheduleTransformers'
import { getCycleDayNumber } from '../../../../entities/BaseSchedule/helpers/baseScheduleCommentHelpers'
import { BaseScheduleShiftViewModel } from '../../../../entities/BaseScheduleShift/BaseScheduleShift'
import addDatesToBaseScheduleShift from '../../../../entities/BaseScheduleShift/helpers/addDatesToBaseScheduleShift'
import { addWeeks, getDaysOfWeek } from '../../../../helpers/date'
import { translate } from '../../../../helpers/translations/translator'
import { AddShiftFormData } from '../../../../models'
import { fetchDepartments } from '../../../../redux/@toolkit/departments/departmentsActions'
import { Reducers } from '../../../../redux/reducers'
import BaseScheduleCalendar from '../../containers/BaseSchedule/BaseScheduleCalendar/BaseScheduleCalendar'
import BaseScheduleSidebar from '../../containers/BaseSchedule/BaseScheduleSidebar/BaseScheduleSidebar'
import AddShift from '../../forms/AddShiftForm/AddShiftForm'

const ConnectedBaseSchedule: FC = (): ReactElement => {
    const dispatch = useDispatch()
    const typedDispatch = useTypedDispatch()
    const { id: baseScheduleIdParam } = useParams<{ id?: string }>()

    const [addShiftsModalIsActive, setAddShiftsModalIsActive] = useState(false)
    const [addShiftDate, setAddShiftDate] = useState<Date>(addWeeks(new Date(), 1))
    const [addCommentsModalIsActive, setAddCommentsModalIsActive] = useState(false)
    const [addCommentDate, setAddCommentDate] = useState<Date>(addWeeks(new Date(), 1))
    const [addShiftWeekNumber, setAddShiftWeekNumber] = useState(1)

    const {
        isLoading,
        isSuccessful,
        lastModifiedBaseScheduleShiftId,
        selectedWeekIndex,
        baseSchedule: baseScheduleDetail,
        error: baseScheduleError,
    } = useTypedSelector(state => state.pacoBaseScheduleReducer)

    const previousBaseScheduleIdRef = useRef(baseScheduleIdParam)

    const totalWeeks = baseScheduleDetail?.totalWeeks ?? 1

    const selectedDaysInWeeks = useMemo(
        () => Array.from({ length: totalWeeks }).map((_, index) => getDaysOfWeek(addWeeks(startOfYear(addYears(new Date(), 1)), index))),
        [totalWeeks],
    )

    const {
        isLoading: isBaseScheduleShiftLoading, isDeleteBaseScheduleShiftSuccessful, deletedBaseScheduleShiftId, isSuccessful: baseScheduleShiftIsSuccessful, isLoading: baseScheduleShiftIsLoading,
    } = useTypedSelector(state => state.pacoBaseScheduleShiftReducer)
    const { isLoading: isDepartmentsLoading, departments } = useSelector((state: Reducers) => state.departmentsReducer)
    const filter = useSelector((state: Reducers) => state.filterReducer.filter)
    const { permissions } = useTypedSelector(state => state.authenticatedUserReducer)

    const baseSchedule = useMemo(() => {
        if (! baseScheduleDetail) {
            return undefined
        }

        if (baseScheduleIdParam && baseScheduleDetail.id !== baseScheduleIdParam) {
            return undefined
        }

        return transformBaseScheduleDetailToViewModel(baseScheduleDetail)
    }, [baseScheduleDetail, baseScheduleIdParam])

    const canAddBaseScheduleShifts = checkPermission(permissions, 'add-new-base-schedule-shifts')
    const canEditBaseScheduleShifts = checkPermission(permissions, 'edit-all-base-schedule-shifts')

    const shiftsWithDatesByWeek = useMemo(
        () => selectedDaysInWeeks.map((weekDays, weekIndex) => (baseSchedule?.shifts || [])
            .filter(shift => shift.weekNumber === weekIndex + 1)
            .map(shift => addDatesToBaseScheduleShift(shift, weekDays))),
        [baseSchedule, selectedDaysInWeeks],
    )

    const handleCopyShiftsSubmit = async (shift: BaseScheduleShiftViewModel) => {
        typedDispatch(copyBaseScheduleShift(shift))
    }

    const handleWeekIndexChange = (weekIndex: number) => {
        typedDispatch(setSelectedWeekIndex(weekIndex))
    }

    const handleOpenAddShiftsModal = (shiftDate: Date) => {
        setAddShiftDate(shiftDate)

        const weekIndex = selectedDaysInWeeks.findIndex(weekDays => weekDays.some(day => isSameDay(day, shiftDate)))
        setAddShiftWeekNumber(weekIndex >= 0 ? weekIndex + 1 : 1)
        setAddShiftsModalIsActive(true)
    }

    const handleCloseAddShiftsModal = () => setAddShiftsModalIsActive(false)

    const handleAddShiftSubmit = (formData: AddShiftFormData) => {
        setAddShiftsModalIsActive(false)

        if (! baseSchedule) {
            return
        }

        typedDispatch(createBaseScheduleShift(formData, addShiftWeekNumber, baseSchedule.id))
    }

    const handleOpenAddCommentModal = (commentDate: Date) => {
        setAddCommentDate(commentDate)
        setAddCommentsModalIsActive(true)
    }

    const handleCloseAddCommentModal = () => setAddCommentsModalIsActive(false)

    const handleAddCommentSubmit = async (formData: AddCommentFormData) => {
        setAddCommentsModalIsActive(false)

        if (! baseSchedule) {
            return
        }

        const weekIndex = selectedDaysInWeeks.findIndex(weekDays => weekDays.some(day => isSameDay(day, addCommentDate)))
        const cycleDayNumber = getCycleDayNumber(weekIndex >= 0 ? weekIndex : 0, addCommentDate)

        await typedDispatch(addBaseScheduleComment(formData.comment, baseSchedule.id, cycleDayNumber))
    }

    const handleDeleteComment = async (commentId: string) => {
        await typedDispatch(deleteBaseScheduleComment(commentId))
    }

    const handleAddWeek = () => {
        if (! baseSchedule) {
            return
        }

        typedDispatch(editBaseSchedule(baseSchedule.id, {
            totalWeeks: baseSchedule.totalWeeks + 1,
        }))
    }

    const handleRemoveWeek = () => {
        if (! baseSchedule) {
            return
        }

        typedDispatch(editBaseSchedule(baseSchedule.id, {
            totalWeeks: baseSchedule.totalWeeks - 1,
        }))
    }

    useEffect((): void => {
        window.scrollTo(0, 0)

        if (! departments.length) {
            typedDispatch(fetchDepartments())
        }
    }, [])

    useEffect(() => {
        if (baseSchedule) {
            setAddShiftDate(selectedDaysInWeeks[0][0])
        }
    }, [baseSchedule])

    useEffect(() => {
        if ((baseScheduleShiftIsSuccessful || isSuccessful) && baseScheduleIdParam) {
            typedDispatch(getBaseSchedule(baseScheduleIdParam))
        }

        handleCloseAddShiftsModal()
        typedDispatch(setIsSuccessful(false))
        typedDispatch(setBaseScheduleShiftIsSuccessful(false))
    }, [baseScheduleShiftIsSuccessful, isSuccessful])

    useEffect(() => {
        if (baseScheduleIdParam) {
            typedDispatch(getBaseSchedule(baseScheduleIdParam))
        }
    }, [baseScheduleIdParam, baseScheduleShiftIsSuccessful])

    useEffect(() => {
        const previousBaseScheduleId = previousBaseScheduleIdRef.current

        if (previousBaseScheduleId && previousBaseScheduleId !== baseScheduleIdParam) {
            typedDispatch(setBaseSchedule(undefined))
            typedDispatch(setSelectedWeekIndex(0))
        }

        previousBaseScheduleIdRef.current = baseScheduleIdParam
    }, [baseScheduleIdParam])

    useEffect(() => {
        typedDispatch(setLastModifiedBaseScheduleShiftId(undefined))
    }, [filter])

    useEffect((): void => {
        if (isDeleteBaseScheduleShiftSuccessful && baseScheduleDetail) {
            const updatedBaseSchedule: BaseScheduleDetail = {
                ...baseScheduleDetail,
                shifts: baseScheduleDetail.shifts?.filter(shift => shift.id !== deletedBaseScheduleShiftId),
            }

            typedDispatch(setBaseSchedule(updatedBaseSchedule))
            typedDispatch(setDeletedBaseScheduleShiftId(undefined))
            typedDispatch(setIsDeleteBaseScheduleShiftSuccessful(false))
        }
    }, [isDeleteBaseScheduleShiftSuccessful])

    useEffect(() => {
        if (! baseScheduleError) {
            return
        }

        dispatch(setToast({
            text: baseScheduleError,
            type: ToastType.fail,
            shouldFadeOutAutomatically: false,
        }))

        dispatch(setError(''))
    }, [baseScheduleError, dispatch])

    return (
        <SidebarPage
            showLegend
            sidebarContent={(
                <BaseScheduleSidebar
                    addShiftDate={selectedDaysInWeeks[0][0]}
                    canAddBaseScheduleShifts={canAddBaseScheduleShifts}
                    onAddShift={handleOpenAddShiftsModal}
                />
            )}
        >
            <BaseScheduleCalendar
                isLoading={isLoading || baseScheduleShiftIsLoading || isDepartmentsLoading || isBaseScheduleShiftLoading}
                baseSchedule={baseSchedule}
                selectedDaysInWeeks={selectedDaysInWeeks}
                shiftsByWeek={shiftsWithDatesByWeek}
                selectedWeekIndex={selectedWeekIndex}
                highlightShiftId={lastModifiedBaseScheduleShiftId}
                canAddBaseScheduleShifts={canAddBaseScheduleShifts}
                canEditBaseScheduleShifts={canEditBaseScheduleShifts}
                onWeekIndexChange={handleWeekIndexChange}
                onAddShift={handleOpenAddShiftsModal}
                onCopyShiftsSubmit={handleCopyShiftsSubmit}
                onDeleteComment={handleDeleteComment}
                onAddComment={handleOpenAddCommentModal}
                onAddWeek={handleAddWeek}
                onRemoveWeek={handleRemoveWeek}
            />

            <Modal
                size="lg"
                isOpen={addShiftsModalIsActive}
                className="concepts-calendar-overview__add-shift-form"
            >
                <AddShift
                    selectedDays={selectedDaysInWeeks.flat()}
                    title={translate('pages.shifts.addShift')}
                    date={addShiftDate}
                    departments={departments}
                    onSubmit={handleAddShiftSubmit}
                    onCancel={handleCloseAddShiftsModal}
                />
            </Modal>

            {addCommentsModalIsActive && (
                <PacoModal>
                    <ModalContent title={trans('common.addComment')}>
                        <AddCommentForm
                            relationshipKey="baseSchedule"
                            relationshipType="baseSchedule"
                            relationshipId={baseSchedule?.id || ''}
                            onSubmit={handleAddCommentSubmit}
                            onCancel={handleCloseAddCommentModal}
                        />
                    </ModalContent>
                </PacoModal>
            )}
        </SidebarPage>
    )
}

export default ConnectedBaseSchedule
